import { Divider, Form, Icon, Input, InputGroup, Select, Text } from '@opengov/component-library/dist';
import { BasicColumnTypes } from '@opengov/ogql-types';
import { IConditionalReportColumn } from '@opengov/reporting-types';
import {
  EConditionalComparisonOperators,
  EReportColumnTypes,
  IConstantReportColumn,
  IReportColumn
} from '@opengov/reporting-types/src/reportColumns-immer';
import { isEqual } from 'lodash';

import React from 'react';

import { ICurrentConditionalSetupRecord } from '../../../../createConditional/state';
import { EAllowedConditionalTypes } from '../../../../createConditional/types';
import { IReportLocale } from '../../../../locale';
import { percentageInputFormatToStorageFormat, percentageStorageFormatToInputFormat } from '../../utilities';
import styles from '../CreateConditionalModal.scss';

export interface IThirdConditionalStep {
  reportLocaleText: IReportLocale;
  valueToCompareOptions: any;
  optionalSecondValueToCompareOptions: any;
  columns: IReportColumn[];
  conditional: IConditionalReportColumn<any>;
  conditionalOptions: any[];
  currentConditionSetup: ICurrentConditionalSetupRecord<any>;
  handleValueSelect(value: any, updaterFunction: (arg0: any) => void): void;
  updateCurrentConditionOperator(operator: EAllowedConditionalTypes): void;
  updateCurrentConditionMatchValue(value: any): void;
  updateCurrentConditionLowerBound(value: any): void;
  updateCurrentConditionUpperBound(value: any): void;
  updateCurrentConditionCustomValue(value: any): void;
  updateCurrentConditionReturnedValue(value: any): void;
  updateElseValue(value: any): void;
}

export const getIconComponent = (operator) => {
  switch (operator) {
    case EConditionalComparisonOperators.greaterThan:
      return <Icon icon="operator-greaterthan" />;
    case EConditionalComparisonOperators.greaterThanOrEqualTo:
      return <Icon icon="operator-greaterthanorequal" />;
    case EConditionalComparisonOperators.lessThan:
      return <Icon icon="operator-lessthan" />;
    case EConditionalComparisonOperators.lessThanOrEqualTo:
      return <Icon icon="operator-lessthanorequal" />;
    case EConditionalComparisonOperators.equalTo:
      return <Icon icon="operator-equal" />;
    case EConditionalComparisonOperators.notEqualTo:
      return <Icon icon="operator-notequal" />;
    case EConditionalComparisonOperators.between:
      return <Icon icon="operator-contains" />;
    default:
      return <Icon icon="operator-equal" />;
  }
};

export class Step3 extends React.Component<IThirdConditionalStep, { maxHeights: Record<string, number> }> {
  constructor(props) {
    super(props);
    this.state = {
      maxHeights: {}
    };
  }

  updateCurrentConditionCustomValue = (value, valueType) => {
    let newValue = value;
    if (this.props.conditional.sourceColumn.type === BasicColumnTypes.Percent) {
      newValue = percentageInputFormatToStorageFormat(value);
    }
    this.props.updateCurrentConditionCustomValue({ value: newValue, valueType });
  };

  updateMaxHeight = () => {
    const listboxes = document.querySelectorAll<HTMLElement>('.auto-height-select [aria-haspopup="listbox"]');
    const windowHeight = globalThis.innerHeight;
    const maxHeights = {};
    listboxes.forEach((listbox) => {
      const clientRect = listbox.getBoundingClientRect();
      const maxHeight = windowHeight - clientRect.bottom;
      maxHeights[listbox.dataset.test] = maxHeight;
    });
    if (!isEqual(maxHeights, this.state.maxHeights)) {
      this.setState({ maxHeights });
    }
  };

  componentDidMount = () => {
    this.updateMaxHeight();
    globalThis.addEventListener('resize', this.updateMaxHeight);
  };

  componentWillUnmount = () => {
    globalThis.removeEventListener('resize', this.updateMaxHeight);
  };

  renderCustomValueInput = (
    columnType,
    customValueColumn: IConstantReportColumn<any>,
    dataTest: string,
    valueType: string
  ) => {
    const customNumberInput = (
      <Input.Number
        dataTest={`${dataTest}-input`}
        placeholder={this.props.reportLocaleText.calculations.conditionalModal.customNumberInputPlaceholder}
        value={
          this.props.conditional.sourceColumn.type === BasicColumnTypes.Percent
            ? customValueColumn.value && percentageStorageFormatToInputFormat(customValueColumn.value)
            : customValueColumn.value
        }
        onChange={(value) => {
          this.updateCurrentConditionCustomValue(value, valueType);
        }}
      />
    );
    const customDateInput = (
      <Input.Date
        dataTest={`${dataTest}-input`}
        value={customValueColumn.value}
        onChange={(value) => {
          this.props.updateCurrentConditionCustomValue({ value, valueType });
        }}
      />
    );
    if (columnType === 'date') {
      return customDateInput;
    } else {
      return customNumberInput;
    }
  };

  renderTypedInput = (value, changeHandler, dataTest) => {
    const {
      conditional,
      reportLocaleText: {
        calculations: {
          conditionalModal: {
            textInputPlaceholder,
            numberInputPlaceholder,
            currencyInputPlaceholder,
            percentageInputPlaceholder
          }
        }
      }
    } = this.props;
    switch (conditional.type) {
      case BasicColumnTypes.Text:
        return (
          <div className={styles.typedInputWrapper}>
            <Input.Text
              size="default"
              placeholder={textInputPlaceholder}
              value={value}
              onChange={changeHandler}
              dataTest={`${dataTest}-input`}
            />
          </div>
        );
      case BasicColumnTypes.Numeric:
        return (
          <div className={styles.typedInputWrapper}>
            <InputGroup>
              <InputGroup.Prefix>
                <InputGroup.Icon icon="datatype-numeric" />
              </InputGroup.Prefix>
              <Input.Number
                dataTest={`${dataTest}-input`}
                placeholder={numberInputPlaceholder}
                value={value}
                onChange={changeHandler}
              />
            </InputGroup>
          </div>
        );
      case BasicColumnTypes.Currency:
        return (
          <div className={styles.typedInputWrapper}>
            <InputGroup>
              <InputGroup.Prefix>
                <InputGroup.Icon icon="datatype-currency" />
              </InputGroup.Prefix>
              <Input.Number
                dataTest={`${dataTest}-input`}
                placeholder={currencyInputPlaceholder}
                value={value}
                onChange={changeHandler}
              />
            </InputGroup>
          </div>
        );
      case BasicColumnTypes.Percent:
        // manipulating this value as a string to avoid inaccurate float math

        return (
          <div className={styles.typedInputWrapper}>
            <InputGroup>
              <InputGroup.Prefix>
                <InputGroup.Icon icon="datatype-percentage" />
              </InputGroup.Prefix>
              <Input.Number
                dataTest={`${dataTest}-input`}
                placeholder={percentageInputPlaceholder}
                value={value ? percentageStorageFormatToInputFormat(value) : null}
                onChange={changeHandler}
              />
            </InputGroup>
          </div>
        );
      case BasicColumnTypes.Date:
        return (
          <div className={styles.typedInputWrapper}>
            <InputGroup>
              <InputGroup.Prefix>
                <InputGroup.Icon icon="datatype-date" />
              </InputGroup.Prefix>
              <Input.Date dataTest={`${dataTest}-input`} value={value} onChange={changeHandler} />
            </InputGroup>
          </div>
        );
      default:
        break;
    }
  };

  betweenIsCurrentOperator = () => {
    const { currentConditionSetup } = this.props;
    return currentConditionSetup && currentConditionSetup.operator === EConditionalComparisonOperators.between;
  };

  getCustomValueInput = (value) => {
    const { currentConditionSetup } = this.props;

    const betweenIsCurrentOperator = this.betweenIsCurrentOperator();

    const matchValueOrLowerBoundIsCustomValue =
      (!betweenIsCurrentOperator &&
        currentConditionSetup.matchValue &&
        currentConditionSetup.matchValue.columnType === EReportColumnTypes.constant) ||
      (betweenIsCurrentOperator &&
        currentConditionSetup.lowerBound &&
        currentConditionSetup.lowerBound.columnType === EReportColumnTypes.constant);

    const upperBoundIsCustomValue =
      currentConditionSetup.upperBound && currentConditionSetup.upperBound.columnType === EReportColumnTypes.constant;

    const firstValueCustomInput =
      matchValueOrLowerBoundIsCustomValue &&
      this.renderCustomValueInput(
        betweenIsCurrentOperator
          ? currentConditionSetup.lowerBound && currentConditionSetup.lowerBound.type
          : currentConditionSetup.matchValue && currentConditionSetup.matchValue.type,
        betweenIsCurrentOperator
          ? (currentConditionSetup.lowerBound as any)
          : (currentConditionSetup.matchValue as any),
        'value-1',
        betweenIsCurrentOperator ? 'lowerBound' : 'matchValue'
      );

    const secondValueCustomInput =
      upperBoundIsCustomValue &&
      this.renderCustomValueInput(
        currentConditionSetup && currentConditionSetup.upperBound && currentConditionSetup.upperBound.type,
        currentConditionSetup && (currentConditionSetup.upperBound as any),
        'value-2',
        'upperBound'
      );

    switch (value) {
      case 'value1':
        return firstValueCustomInput;
      case 'value2':
        return secondValueCustomInput;
      default:
        break;
    }
  };

  updateCurrentConditionReturnedValue = (value) => {
    let newValue = value;
    if (this.props.conditional.type === BasicColumnTypes.Percent) {
      newValue = percentageInputFormatToStorageFormat(value);
    }
    this.props.updateCurrentConditionReturnedValue(newValue);
  };

  updateElseValue = (value) => {
    let newValue = value;
    if (this.props.conditional.type === BasicColumnTypes.Percent) {
      newValue = percentageInputFormatToStorageFormat(value);
    }
    this.props.updateElseValue(newValue);
  };

  renderSelectValue1() {
    const {
      valueToCompareOptions,
      conditional,
      currentConditionSetup,
      reportLocaleText: {
        calculations: {
          conditionalModal: { step3SelectValuePlaceholder }
        }
      },
      updateCurrentConditionMatchValue,
      updateCurrentConditionLowerBound
    } = this.props;

    const betweenIsCurrentOperator =
      currentConditionSetup && currentConditionSetup.operator === EConditionalComparisonOperators.between;
    const maxHeight = this.state?.maxHeights?.[`value-1-select-menu`];

    return (
      <Select
        maxMenuHeight={maxHeight}
        className="auto-height-select"
        placeholder={step3SelectValuePlaceholder}
        value={
          betweenIsCurrentOperator
            ? currentConditionSetup && currentConditionSetup.lowerBound && currentConditionSetup.lowerBound.name
            : currentConditionSetup && currentConditionSetup.matchValue && currentConditionSetup.matchValue.name
        }
        dataTest="value-1-select-menu"
        onChange={(value) => {
          this.props.handleValueSelect(
            value,
            betweenIsCurrentOperator ? updateCurrentConditionLowerBound : updateCurrentConditionMatchValue
          );
        }}
      >
        {[
          ...[...(Object as any).values(EAllowedConditionalTypes), 'custom'].reduce((options, columnType) => {
            if (valueToCompareOptions[columnType]) {
              if (columnType !== 'custom') {
                options.push([
                  <Select.Label
                    key={`value-1-${columnType}-section-label`}
                    text={(columnType as string).toUpperCase()}
                    disabled={valueToCompareOptions[columnType].disabled}
                  />,
                  ...valueToCompareOptions[columnType].items.map((option, i) => {
                    return (
                      <Select.Option
                        disabled={
                          option.disabled ||
                          option.value === conditional.sourceColumn.name ||
                          (betweenIsCurrentOperator &&
                            currentConditionSetup.upperBound &&
                            option.value === currentConditionSetup.upperBound.name)
                        }
                        dataTest={`value-1-${columnType}-option-${i}`}
                        key={`value-1-option-${option.value}`}
                        label={option.label}
                        value={option.value}
                        secondaryLabel={option.secondaryLabel}
                      />
                    );
                  }),
                  <Select.Divider key={`value-1-option-${columnType}-divider`} />
                ]);
              } else {
                options.push([
                  ...valueToCompareOptions[columnType].items.map((option, i) => {
                    return (
                      <Select.Option
                        disabled={option.disabled}
                        dataTest={`value-1-${columnType}-option-${i}`}
                        key={`value-1-option-${option.value}`}
                        label={option.label}
                        value={option.value}
                      />
                    );
                  })
                ]);
              }
            }
            return options;
          }, [])
        ]}
      </Select>
    );
  }

  renderSelectValue2() {
    const {
      optionalSecondValueToCompareOptions,
      conditional,
      currentConditionSetup,
      reportLocaleText: {
        calculations: {
          conditionalModal: { step3SelectValuePlaceholder }
        }
      },
      updateCurrentConditionUpperBound
    } = this.props;
    const maxHeight = this.state?.maxHeights?.['value-2-select-menu'];

    if (!maxHeight) {
      this.updateMaxHeight();
    }

    return (
      <Select
        maxMenuHeight={maxHeight}
        className="auto-height-select"
        placeholder={step3SelectValuePlaceholder}
        suffix={<div />}
        value={currentConditionSetup && currentConditionSetup.upperBound && currentConditionSetup.upperBound.name}
        dataTest="value-2-select-menu"
        onChange={(value) => {
          this.props.handleValueSelect(value, updateCurrentConditionUpperBound);
        }}
      >
        {[
          ...[...(Object as any).values(EAllowedConditionalTypes), 'custom'].reduce((options, columnType) => {
            if (optionalSecondValueToCompareOptions[columnType]) {
              if (columnType !== 'custom') {
                options.push([
                  <Select.Label
                    key={`value-2-${columnType}-section-label`}
                    text={(columnType as string).toUpperCase()}
                    disabled={optionalSecondValueToCompareOptions[columnType].disabled}
                  />,
                  ...optionalSecondValueToCompareOptions[columnType].items.map((option, i) => {
                    return (
                      <Select.Option
                        disabled={
                          option.disabled ||
                          option.value === conditional.sourceColumn.name ||
                          (currentConditionSetup.lowerBound && option.value === currentConditionSetup.lowerBound.name)
                        }
                        dataTest={`value-2-${columnType}-option-${i}`}
                        key={`value-2-option-${option.value}`}
                        label={option.label}
                        value={option.value}
                        secondaryLabel={option.secondaryLabel}
                      />
                    );
                  }),
                  <Select.Divider key={`value-2-option-${columnType}-divider`} />
                ]);
              } else {
                options.push([
                  ...optionalSecondValueToCompareOptions[columnType].items.map((option, i) => {
                    return (
                      <Select.Option
                        disabled={option.disabled}
                        dataTest={`value-2-${columnType}-option-${i}`}
                        key={`value-2-option-${option.value}`}
                        label={option.label}
                        value={option.value}
                      />
                    );
                  })
                ]);
              }
            }
            return options;
          }, [])
        ]}
      </Select>
    );
  }

  render() {
    const {
      conditional,
      conditionalOptions,
      currentConditionSetup,
      reportLocaleText: {
        calculations: {
          conditionalModal: { step3SelectConditionPlaceholder, step3BTitle, wordAnd, wordThen, wordElse, wordValue }
        }
      },
      updateCurrentConditionOperator
    } = this.props;

    const betweenIsCurrentOperator =
      currentConditionSetup && currentConditionSetup.operator === EConditionalComparisonOperators.between;

    const maxHeight = this.state?.maxHeights?.['condition-select-menu'];

    return (
      <div>
        <div className={styles.flexHorizontalInputRow}>
          <Form.Item label={step3SelectConditionPlaceholder}>
            <Select
              className="auto-height-select"
              placeholder={step3SelectConditionPlaceholder}
              value={currentConditionSetup && currentConditionSetup.operator}
              dataTest="condition-select-menu"
              onChange={updateCurrentConditionOperator}
              maxMenuHeight={maxHeight}
            >
              {conditionalOptions.map((option) => (
                <Select.Option
                  prefixIcon={getIconComponent(option.value)}
                  disabled={option.disabled}
                  dataTest={`condition-option-${option.value}`}
                  key={`condition-option-${option.value}`}
                  label={option.label}
                  value={option.value}
                />
              ))}
            </Select>
          </Form.Item>
          <div className={styles.oneUnitSpacer} />
          <div className={styles.flexVertical}>
            <div className={styles.flexHorizontal}>
              <Form.Item label={betweenIsCurrentOperator ? `${wordValue} 1` : wordValue}>
                <div className={styles.flexHorizontalInputRow}>
                  {this.getCustomValueInput('value1') ? (
                    <InputGroup>
                      <InputGroup.Prefix>{this.renderSelectValue1()}</InputGroup.Prefix>
                      {this.getCustomValueInput('value1')}
                    </InputGroup>
                  ) : (
                    this.renderSelectValue1()
                  )}
                </div>
              </Form.Item>
            </div>
          </div>
          {betweenIsCurrentOperator && (
            <div className={styles.flexHorizontalInputRow}>
              <div className={styles.oneUnitSpacer} />
              <div className={styles.textCenteredWithExtraTopPadding}>
                <Text>{wordAnd}</Text>
              </div>
              <div className={styles.oneUnitSpacer} />
              <div className={styles.flexHorizontalInputRow}>
                <Form.Item label={`${wordValue} 2`}>
                  <div className={styles.flexHorizontalInputRow}>
                    {this.getCustomValueInput('value2') ? (
                      <InputGroup>
                        <InputGroup.Prefix>{this.renderSelectValue2()}</InputGroup.Prefix>
                        {this.getCustomValueInput('value2')}
                      </InputGroup>
                    ) : (
                      this.renderSelectValue2()
                    )}
                  </div>
                </Form.Item>
              </div>
            </div>
          )}
        </div>
        <div className={styles.twoUnitSpacer} />
        <div className={styles.flexHorizontal}>
          <div className={styles.textCenteredWithTopPadding}>
            <Text>
              <strong>{wordThen}</strong>
            </Text>
          </div>
          <div className={styles.oneUnitSpacer} />
          {this.renderTypedInput(currentConditionSetup.returnedValue, this.updateCurrentConditionReturnedValue, 'then')}
        </div>
        <div className={styles.twoUnitSpacer} />
        <Divider.Horizontal />
        <div className={styles.twoUnitSpacer} />
        <Text>{step3BTitle}</Text>
        <div className={styles.twoUnitSpacer} />
        <div className={styles.flexHorizontal}>
          <div className={styles.textCenteredWithTopPadding}>
            <Text>
              <strong>{wordElse}</strong>
            </Text>
          </div>
          <div className={styles.oneUnitSpacer} />
          {this.renderTypedInput(conditional.else, this.updateElseValue, 'else')}
        </div>
      </div>
    );
  }
}
